import { ErrorBoundary as BaseErrorBoundary, type ErrorBoundaryProps } from '@sentry/react';
import { type PropsWithChildren, useMemo } from 'react';
import { OnErrorContext, useOnErrorContext } from './OnErrorContext';

// Next signals redirect()/notFound() by throwing an error tagged with a `digest`.
// NEXT_REDIRECT = redirect(); NEXT_HTTP_ERROR_FALLBACK = notFound()/forbidden()/unauthorized().
const isNextSignal = (error: unknown): error is { digest?: unknown } => {
  const digest = (error as { digest?: unknown } | null)?.digest;
  return (
    typeof digest === 'string' &&
    (digest.startsWith('NEXT_REDIRECT') || digest.startsWith('NEXT_HTTP_ERROR_FALLBACK'))
  );
};

// Sentry's boundary would catch those signals and render a fallback, swallowing the
// navigation. Rethrow so Next handles it. Hand-rolled `unstable_rethrow` (carbon is
// shared with matrix-native, which has no next/navigation).
function rethrowNextControlFlow(
  fallback: ErrorBoundaryProps['fallback'],
): ErrorBoundaryProps['fallback'] {
  return (errorData) => {
    if (isNextSignal(errorData.error)) throw errorData.error;
    return <>{typeof fallback === 'function' ? fallback(errorData) : fallback}</>;
  };
}

function ErrorBoundary({ children, ...restProps }: React.PropsWithChildren<ErrorBoundaryProps>) {
  const { onError: onErrorContext } = useOnErrorContext();

  return (
    <BaseErrorBoundary
      {...restProps}
      fallback={rethrowNextControlFlow(restProps.fallback)}
      onError={onErrorContext}
    >
      {children}
    </BaseErrorBoundary>
  );
}

function ErrorBoundaryWithErrorContext({
  children,
  ...restProps
}: PropsWithChildren<ErrorBoundaryProps> & {
  onError?: (error: unknown) => void;
}) {
  const value = useMemo(() => ({ onError: restProps.onError }), [restProps.onError]);
  return (
    <BaseErrorBoundary
      {...restProps}
      fallback={rethrowNextControlFlow(restProps.fallback)}
      onError={restProps.onError}
    >
      <OnErrorContext.Provider value={value}>{children}</OnErrorContext.Provider>
    </BaseErrorBoundary>
  );
}

ErrorBoundary.displayName = 'UAErrorBoundary';
ErrorBoundaryWithErrorContext.displayName = 'UAErrorBoundaryWithErrorContext';

export { ErrorBoundary, ErrorBoundaryWithErrorContext };
